import React, { useRef, useState } from "react";
import moment from "moment";
import { useNavigate, useSearchParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useAppConfig } from "@/appConfig";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import { GitchaNextSteps } from "@/components/ResultsGitcha";
import ResultsCombined from "@/components/ResultsCombined";
import WinsTile from "@/components/ResultsWins";
import ResultsRealtime from "@/components/ResultsRealtime";
import ResultsLeads from "@/components/ResultsLeads";
import { Headline, Overview, ResultsSkeleton } from "@/components/ResultsParts";
import { gitchaFeedLine, networkFeedLine, ownBioHref } from "@/helpers/resultsCopy";
import { getUserDetails } from "@/services/userprofileService";
import {
  SOURCES,
  analyticsHeadline,
  buildSeries,
  buildTiles,
  isSettledFor,
  mergeFeed,
  mergeRealtime,
  mergeTopListings,
  parseSources,
  settleSnapshot,
  toggleSource,
} from "@/helpers/analyticsMerge";
import { getGitchaResults, getNetworkResults, getWins } from "@/services/resultsService";
import OfficeResults from "@/components/OfficeResults";
import { useManagedBrokerages } from "@/hooks/useBrokerageManage";
import { plansPathFor } from "@/helpers/brokeragePlanPath";
import PeriodMenu from "./PeriodMenu";
import ScopeMenu from "./ScopeMenu";
import SourcesFilter from "./SourcesFilter";
import { keepInPlace } from "./scrollAnchor";
import "./ResultsPage.scss";

export { RESULTS_PATH } from "@/helpers/resultsCopy";

const DEFAULT_DAYS = 30;


const ACTIVITY = {
  "network,gitcha": "Views, introductions and contacts",
  network: "Views, introductions and messages",
  gitcha: "Views and contacts",
};

const liveBlock = (bySource) =>
  bySource.network
    ? {
        value: bySource.network.buySide?.matchesForBuyers,
        label: "Listings matching your buyers right now",
        detail:
          bySource.network.sellSide?.buyersMatchingProperties != null
            ? `${Number(bySource.network.sellSide.buyersMatchingProperties).toLocaleString("en-US")} Buyer Want-Listings match your properties`
            : null,
      }
    : {
        value: bySource.gitcha?.presence?.syndicated || 0,
        label: "Buyer Want-Listings public on Gitcha.com",
        detail: bySource.gitcha?.presence?.activeWantListings ? `of ${bySource.gitcha.presence.activeWantListings} active` : null,
      };

const ResultsPage = () => {
  const [params, setParams] = useSearchParams();
  const { tenant } = useAppConfig() || {};
  const { isGitchaDefaultWebsite } = useCurrentWebsite();
  const network = tenant?.mlsShortName || "MLS";
  const [days, setDays] = useState(DEFAULT_DAYS);

  const navigate = useNavigate();
  const available = isGitchaDefaultWebsite ? ["gitcha"] : SOURCES;
  const sources = parseSources(params, available);
  const managedQuery = useManagedBrokerages(!isGitchaDefaultWebsite);
  const managed = managedQuery.data || [];
  const scopePending = !isGitchaDefaultWebsite && Boolean(managedQuery.isPending) && !managedQuery.isError;
  const controlsRef = useRef(null);
  const settledRef = useRef(null);
  const office = managed.find((item) => item.id === Number(params.get("office"))) || null;
  const winsOpen = params.get("wins") === "open" || params.get("tab") === "wins";

  const userQuery = useQuery({ queryKey: ["userDetails"], queryFn: getUserDetails });
  const bioHref = ownBioHref(userQuery.data?.result?.[0]?.slug);

  const networkQuery = useQuery({
    queryKey: ["results", "network", days],
    queryFn: () => getNetworkResults(days),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
    enabled: sources.includes("network") && !office,
  });
  const gitchaQuery = useQuery({
    queryKey: ["results", "gitcha", days],
    queryFn: () => getGitchaResults(days),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
    enabled: sources.includes("gitcha") && !office,
  });
  const winsQuery = useQuery({ queryKey: ["results", "wins"], queryFn: getWins, staleTime: 60 * 1000 });
  const winsOn = Boolean(winsQuery.data?.enabled);

  const queries = { network: networkQuery, gitcha: gitchaQuery };
  const active = sources.map((source) => queries[source]);
  const snapshot = settleSnapshot(settledRef.current, {
    sources,
    days,
    entries: Object.fromEntries(
      sources.map((source) => [source, { data: queries[source].data, placeholder: queries[source].isPlaceholderData }]),
    ),
  });
  settledRef.current = snapshot;
  const ready = Boolean(snapshot);
  const switching = ready && !isSettledFor(snapshot, sources, days);
  const failed = active.find((query) => query.isError && (!query.data || query.isPlaceholderData));
  const bySource = snapshot ? snapshot.bySource : {};

  const updateParams = (change, options = { replace: true }) => {
    const next = new URLSearchParams(params);
    if (next.get("tab") === "wins") next.set("wins", "open");
    next.delete("tab");
    change(next);
    setParams(next, options);
  };

  const onToggleSource = (source) =>
    keepInPlace(controlsRef.current, () =>
      updateParams((next) => {
        const picked = toggleSource(sources, source);
        if (picked.length === available.length) next.delete("sources");
        else next.set("sources", picked.join(","));
      }),
    );

  const onToggleWins = (event) =>
    keepInPlace(event?.currentTarget || null, () =>
      updateParams(
        (next) => {
          if (winsOpen) next.delete("wins");
          else next.set("wins", "open");
        },
        { replace: false },
      ),
    );

  const options = available.map((key) => ({ key, label: key === "network" ? `${network} network` : "Gitcha.com" }));

  const onScope = (officeId) =>
    updateParams((next) => {
      if (officeId) next.set("office", String(officeId));
      else next.delete("office");
    });

  let body;
  if (office) {
    body = (
      <OfficeResults
        key={office.id}
        brokerageId={office.id}
        name={office.name}
        networkName={network}
        days={days}
        sources={sources}
        onSeePlan={() => navigate(plansPathFor(office.id))}
      />
    );
  } else if (failed) {
    body = (
      <section className="results-card results-page__error">
        <p className="results-page__error-title">Your analytics didn't load</p>
        <p className="results-page__error-body">Check your connection and try again.</p>
        <button type="button" className="btn btn-primary" onClick={() => failed.refetch()}>
          Try again
        </button>
      </section>
    );
  } else if (!ready) {
    body = <ResultsSkeleton />;
  } else {
    const period = Object.values(bySource)[0]?.days || snapshot.days;
    const tiles = buildTiles(bySource, network);
    const headline = analyticsHeadline(tiles, bySource, period, network);
    const feed = mergeFeed(bySource).filter((item) => item.kind !== "match");
    const topListings = mergeTopListings(bySource);
    const latest = feed.slice(0, 3).map((item) => ({
      key: `${item.feedKind}-${item.id || item.introductionId || item.listingKey}-${item.at}`,
      title: item.feedKind === "gitcha" ? gitchaFeedLine(item).title : networkFeedLine(item, network).title,
      when: moment.utc(item.at).local().fromNow(),
    }));

    body = (
      <div className={switching ? "results-page__content is-refreshing" : "results-page__content"} aria-busy={switching || undefined}>
        <Headline title={headline.title} sub={headline.sub} />

        <Overview
          label="Analytics"
          series={buildSeries(bySource, network)}
          days={period}
          tiles={tiles}
          below={
            <>
              <ResultsLeads items={feed} kind={sources.length > 1 ? "all" : sources[0]} networkName={network} />
            </>
          }
          aside={
            <div className="results-page__side">
              {winsOn ? (
                <WinsTile
                  data={winsQuery.data}
                  networkName={network}
                  open={winsOpen}
                  onToggle={onToggleWins}
                />
              ) : null}
              <ResultsRealtime
                realtime={mergeRealtime(bySource)}
                topListings={topListings}
                activityLabel={ACTIVITY[sources.join(",")]}
                latest={latest}
                live={liveBlock(bySource)}
                share
              />
            </div>
          }
        />

        <ResultsCombined
          bySource={bySource}
          days={period}
          networkName={network}
          bioHref={bioHref}
          publishedOn={isGitchaDefaultWebsite ? "Gitcha.com" : `the ${network} Buyer Listing Service and Gitcha.com`}
          shareListing={topListings[0] || null}
        />

        {bySource.gitcha ? <GitchaNextSteps presence={bySource.gitcha.presence} /> : null}
      </div>
    );
  }

  return (
    <div className="results-page">
      <div className="results-page__head">
        <div className="results-page__heading">
          <h1 className="results-page__title">Analytics</h1>
          <p className="results-page__subtitle">
            {office ? `Everyone at ${office.name}. Only managers can see this.` : "Only you can see this."}
          </p>
        </div>
        <div
          ref={controlsRef}
          className={scopePending ? "results-page__controls is-pending" : "results-page__controls"}
          aria-hidden={scopePending || undefined}
        >
          {managed.length ? <ScopeMenu offices={managed} value={office?.id || null} onChange={onScope} /> : null}
          <SourcesFilter options={options} selected={sources} onToggle={onToggleSource} />
          <PeriodMenu value={days} onChange={setDays} />
        </div>
      </div>


      <div className="results-page__body">{body}</div>
    </div>
  );
};

export default ResultsPage;
